/**
 * ConditionHealthStrip — Phase C3 of the condition-monitoring PRD.
 *
 * The surface that makes the stability principle legible: "Initiative A remains
 * our highest commitment while conditions X, Y and Z hold" — with each
 * condition's current state beside it, so adapting is a response to a named
 * change rather than whiplash.
 *
 * Read-only and derived. It renders what the record already says and offers no
 * control that changes anything: moving a condition happens in
 * ConditionsPanel, through the RPC, by a human. A strip that could resolve its
 * own warnings would be the system disposing of its own proposals.
 *
 * Every judgment here — breached, stale, the rollup wording — comes from
 * `supabase/functions/_shared/conditionHealthCore.ts`, the same module the
 * ambient watcher uses, so the strip and the docket can never disagree.
 */

import { useMemo } from 'react';
import { AlertTriangle, CircleDot, CircleSlash, Clock, MinusCircle } from 'lucide-react';
import { Panel } from '../../ds';
import type { Tone } from '../../mono/primitives';
import {
  CONDITION_KIND_LABELS,
  describeThreshold,
  judgeCondition,
  rollUpConditions,
  toConditionInput,
  type ConditionInput,
  type ConditionStatus,
} from '../../../lib/conditionObjects';
import type { ConditionHealthRow } from '../../../lib/conditionObjectsService';

export interface ConditionHealthStripProps {
  rows: ConditionHealthRow[];
  /** Names the thing the conditions hang on, for the summary line. */
  subjectLabel?: string;
  /** Workspace review cadence in days; falls back to the core's default. */
  staleAfterDays?: number;
  /** Injected for tests; defaults to now. */
  now?: string;
  /** Hide the panel chrome when embedding inside an existing panel. */
  bare?: boolean;
  className?: string;
}

const STATUS_TONE: Record<ConditionStatus, Tone> = {
  holding: 'green',
  in_question: 'mustard',
  broken: 'coral',
  retired: 'neutral',
};

const STATUS_ICON: Record<ConditionStatus, React.ComponentType<{ className?: string }>> = {
  holding: CircleDot,
  in_question: Clock,
  broken: AlertTriangle,
  retired: MinusCircle,
};

const TONE_TEXT: Record<Tone, string> = {
  green: 'text-[var(--green)] dark:text-[var(--text-ter)]',
  mustard: 'text-[var(--mustard)]',
  coral: 'text-[var(--coral)]',
  slate: 'text-[var(--text-ter)]',
  neutral: 'text-[var(--text-ter)]',
};

function ConditionRow({
  condition,
  now,
  staleAfterDays,
}: {
  condition: ConditionInput;
  now: string;
  staleAfterDays?: number;
}) {
  const health = judgeCondition(condition, { now, staleAfterDays });
  // Breach is a fact about numbers; the status is what a human recorded. When
  // they disagree, the strip shows both rather than picking one.
  const tone: Tone = health.breached ? 'coral' : STATUS_TONE[condition.status];
  const Icon = health.breached ? AlertTriangle : STATUS_ICON[condition.status];

  return (
    <li className="flex gap-3 border-b border-[var(--rule)] py-3 last:border-b-0">
      <Icon className={`mt-0.5 h-4 w-4 flex-shrink-0 ${TONE_TEXT[tone]}`} aria-hidden="true" />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
          <span className="font-mono text-xs uppercase tracking-[0.12em] text-[var(--text-ter)]">
            {CONDITION_KIND_LABELS[condition.kind]}
          </span>
          <span className={`font-mono text-xs uppercase tracking-[0.12em] ${TONE_TEXT[tone]}`}>
            {health.label}
          </span>
          {health.breached && (
            <span className="font-mono text-xs uppercase tracking-[0.12em] text-[var(--coral)]">
              Past its bound
            </span>
          )}
        </div>
        <p className="mt-1 text-sm text-[var(--text-pri)]">{condition.statement}</p>
        {condition.threshold && (
          <p className="mt-0.5 text-xs text-[var(--text-ter)]">
            Stated as {describeThreshold(condition.threshold)}
            {condition.lastObservedValue !== null &&
              ` · last reported ${condition.lastObservedValue}${condition.threshold.unit ?? ''}`}
            {condition.lastObservedAt && ` on ${condition.lastObservedAt.slice(0, 10)}`}
          </p>
        )}
        <p className="mt-0.5 text-xs text-[var(--text-sec)]">{health.sentence}</p>
      </div>
    </li>
  );
}

export default function ConditionHealthStrip({
  rows,
  subjectLabel,
  staleAfterDays,
  now,
  bare = false,
  className = '',
}: ConditionHealthStripProps) {
  const nowIso = now ?? new Date().toISOString();
  const conditions = useMemo(() => rows.map(toConditionInput), [rows]);
  const rollup = useMemo(
    () => rollUpConditions(conditions, { now: nowIso, staleAfterDays }),
    [conditions, nowIso, staleAfterDays],
  );

  if (rows.length === 0) return null;

  // The commitment sentence, built only from live conditions — a retired claim
  // is not something the commitment still rests on.
  const live = conditions.filter((c) => c.status === 'holding' || c.status === 'in_question');
  const holdsWhile =
    live.length > 0
      ? `${subjectLabel ?? 'This commitment'} remains committed while ${live.length} condition${live.length === 1 ? '' : 's'} hold${live.length === 1 ? 's' : ''}.`
      : null;

  const body = (
    <div className={bare ? className : ''}>
      {holdsWhile && (
        <p className="mb-2 text-sm text-[var(--text-sec)]">{holdsWhile}</p>
      )}
      {rollup.headline && (
        <p
          className={`mb-3 font-mono text-xs uppercase tracking-[0.12em] ${
            rollup.inQuestion + rollup.broken + rollup.breached + rollup.stale > 0
              ? 'text-[var(--mustard)]'
              : 'text-[var(--green)] dark:text-[var(--text-ter)]'
          }`}
        >
          {rollup.headline}
        </p>
      )}
      <ul className="list-none">
        {conditions.map((condition) => (
          <ConditionRow
            key={condition.id}
            condition={condition}
            now={nowIso}
            staleAfterDays={staleAfterDays}
          />
        ))}
      </ul>
      <p className="mt-3 flex items-start gap-2 text-xs text-[var(--text-ter)]">
        <CircleSlash className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
        <span>
          Nothing here changes on its own. A condition moves only when someone moves it, with a
          stated basis.
        </span>
      </p>
    </div>
  );

  if (bare) return body;

  return (
    <Panel label="Conditions" title="Condition health" count={rows.length} className={className}>
      {body}
    </Panel>
  );
}
